import axios from "axios";
import { API_BASE_URL, API_PREFIX } from "@/constants";

// Per-request opt-outs from the global redirect behavior. A page that wants
// to render its own error UI can pass these in the request config, e.g.:
//   apiClient.get("/projects/", { skipServerErrorRedirect: true })
// The interceptor below skips the corresponding window.location.replace
// so the caller's catch/allSettled branch can render inline.
declare module "axios" {
  export interface AxiosRequestConfig {
    skipServerErrorRedirect?: boolean;
    skipNotFoundRedirect?: boolean;
  }
}

export const apiClient = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Server-error statuses handled by the dedicated error page.
const SERVER_ERROR_STATUSES = new Set([500, 502, 503]);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error.config ?? {};
    const url: string = config.url ?? "";
    const isAuthEndpoint = url.startsWith("/auth/");
    const status: number | undefined = error.response?.status;

    if (status === 401 && !isAuthEndpoint) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      // Use a SPA-friendly logout: dispatch event so AuthProvider clears
      // user state and ProtectedRoute redirects via <Navigate> (no hard
      // navigation, so no beforeunload popup on /chat).
      window.dispatchEvent(new CustomEvent("auth:logout"));
    }
    // Only redirect to /404 for non-auth endpoints — auth/SSO 404s are
    // meaningful application states (e.g. "no account yet, please sign up")
    // and must be handled by the caller so the user sees a friendly message.
    if (status === 404 && !isAuthEndpoint && !config.skipNotFoundRedirect) {
      window.dispatchEvent(new CustomEvent("server:not-found"));
    }

    // Full-page redirect on 500/502/503 — friendly error screen instead of
    // an unhandled exception. Callers that want to render an INLINE error
    // (e.g. a dashboard with multiple widgets where only one fails) opt out
    // via `skipServerErrorRedirect: true` in the request config.
    if (
      status &&
      SERVER_ERROR_STATUSES.has(status) &&
      !isAuthEndpoint &&
      !config.skipServerErrorRedirect
    ) {
      // Prevent redirect loops when the error page itself loads data.
      if (!window.location.pathname.startsWith("/server-error")) {
        window.dispatchEvent(new CustomEvent("server:error", { detail: { status } }));
      }
    }

    return Promise.reject(error);
  },
);
